import { Controller } from '../utils/controller';

interface ILoading {
  element: Element | null | undefined;
  state: boolean;
}

interface IUpdateQuantityParams {
  newQty: number;
}

interface ICollection {
  cartId: string;
  collectionId: string;
  collectionName: string;
  collectionType: string;
  collectionSubtype: string;
}

interface IRequestParams {
  collectionData?: ICollection;
  offerCode?: string;
  quantity?: number;
  skus?: string | string[];
}

export default class extends Controller {
  static values = {
    cartLimit: Boolean,
    collection: Object,
    offerCode: String,
    qty: Number,
    qtyLimit: Number,
    skuId: String,
    thumbnailAlternatives: Array,
    type: String,
    dataEndpoint: '/cart/v3_0/drawer/'
  };

  declare cartLimitValue: boolean;
  declare collectionValue: ICollection;
  declare dataEndpointValue: string;
  declare offerCodeValue: string;
  declare qtyValue: number;
  declare qtyLimitValue: number;
  declare skuIdValue: string;
  declare typeValue: string;

  connect(): void {
    this.handleImageLoading();
    this.handleQtyBtnState();
  }

  checkLimit(): void {
    const quantityLimitElement = this.element?.shadowRoot?.querySelector(
      '.cart-item-quantity-limit-wrapper'
    );
    if (quantityLimitElement && this.qtyValue === this.qtyLimitValue) {
      quantityLimitElement.classList.remove('hidden');
    }
  }

  hideLimit(): void {
    const quantityLimitElement = this.element?.shadowRoot?.querySelector(
      '.cart-item-quantity-limit-wrapper'
    );
    if (quantityLimitElement && !quantityLimitElement.classList.contains('hidden')) {
      quantityLimitElement.classList.add('hidden');
    }
  }

  private loading(element: ILoading['element'], state: ILoading['state']): void {
    element?.classList[state ? 'add' : 'remove']('loading');
  }

  async updateQuantity({ params: { newQty } }: { params: IUpdateQuantityParams }) {
    if (newQty !== 0 && this.disabled(newQty)) {
      return;
    }

    const requestBody = this.buildUpdateRequest(newQty);
    const cartItemElement = this.element?.shadowRoot?.querySelector('.cart-item-container');

    this.loading(cartItemElement, true);

    fetch(this.dataEndpointValue, requestBody)
      .then(() => {
        try {
          this.qtyValue = newQty;
          const event = new CustomEvent('fetchCartDrawer');
          window.dispatchEvent(event);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Could not dispatch event', error);
          this.loading(cartItemElement, false);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Could not update item', this.skuIdValue, 'reason', error);
        this.loading(cartItemElement, false);
      });
  }

  async removeItem() {
    await this.updateQuantity({ params: { newQty: 0 } });
  }

  private buildUpdateRequest(newQty: number) {
    const isOfferItem = this.offerCodeValue !== '';
    const isCollectionItem = Object.entries(this.collectionValue).length > 0;

    const requestParams: IRequestParams = isOfferItem
      ? { offerCode: this.offerCodeValue }
      : { quantity: newQty, skus: [this.skuIdValue] };

    if (isCollectionItem) {
      requestParams.skus = this.skuIdValue;
      requestParams['collectionData'] = this.collectionValue;
    }

    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': this.csrfToken
      },
      body: JSON.stringify(requestParams)
    };
  }

  private handleImageLoading(): void {
    const images = this.element?.querySelectorAll('img');
    if (images?.length > 0) {
      images.forEach((image) => {
        image.addEventListener('load', () => {
          image.classList.add('loaded');
        });
        image.addEventListener('error', this.checkImage.bind(this, image));
      });
    }
  }

  private handleQtyBtnState(): void {
    const className = '.cart-item__quantity-button';
    const increaseButton = this.element?.shadowRoot?.querySelector(`${className}--up`);
    const decreaseButton = this.element?.shadowRoot?.querySelector(`${className}--down`);

    if (this.increaseDisabled && increaseButton) {
      increaseButton.ariaDisabled = 'true';
      increaseButton.classList.add('disabled');
    }

    if (this.decreaseDisabled && decreaseButton) {
      decreaseButton.ariaDisabled = 'true';
      decreaseButton.classList.add('disabled');
    }
  }

  private checkImage(element): void {
    element.classList.remove('loaded');
    const alternatives = JSON.parse(
      element?.getAttribute('data-cart-item-thumbnail-alternatives-value') || '[]'
    );
    if (alternatives.length > 0) {
      const alternative = alternatives.shift();
      if (alternative) {
        element.src = alternative;
        element.setAttribute(
          'data-cart-item-thumbnail-alternatives-value',
          JSON.stringify(alternatives)
        );
      }
    }
  }

  private disabled(newQty: number) {
    return newQty > this.qtyValue ? this.increaseDisabled : this.decreaseDisabled;
  }

  private get increaseDisabled(): boolean {
    return this.cartLimitValue || this.qtyValue === this.qtyLimitValue;
  }

  private get decreaseDisabled(): boolean {
    return this.qtyValue === 1;
  }
}
